import {alpha, Grid, Skeleton, Theme, useMediaQuery} from "@mui/material";
import Typography from "@mui/material/Typography";
import {getRandomNumber} from "../../../../util/utils";
import {IHomeWhyGenie} from "../../../../store/home/Helper";
import React, {FC, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import Box from "@mui/material/Box";
import {useTheme} from "@mui/material/styles";
import MarkdownWrapper from "../../../shared/markdown-wrapper/MarkdownWrapper";

interface IProps {
    loading: boolean;
    item: IHomeWhyGenie;
}

const WhyGenieGridItem: FC<IProps> = (props) => {
    const {i18n} = useTranslation();
    const [isFlipped, setIsFlipped] = useState<boolean>(false);
    const matchesSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
    const hiddenBoxRef = useRef<HTMLDivElement>(null);
    const frontSideBoxRef = useRef<HTMLDivElement>(null);
    const [calculatedHeight, setCalculatedHeight] = useState<string>("100%");
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        const measureBoxHeight = () => {
            if (hiddenBoxRef.current && frontSideBoxRef.current && !props.loading && isImageLoaded) {
                const height = Math.max(
                    hiddenBoxRef.current.scrollHeight,
                    frontSideBoxRef.current.scrollHeight
                );
                setCalculatedHeight(`${height}px`);
            }
        };

        measureBoxHeight();
        window.addEventListener("resize", measureBoxHeight);

        return () => {
            window.removeEventListener("resize", measureBoxHeight);
        };
    }, [props.item, i18n.language, props.loading, isImageLoaded]);

    const handleImageLoad = () => {
        setIsImageLoaded(true);
    };

    const handleOnClick = () => {
        if (!matchesSm) {
            setIsFlipped((flipped) => !flipped);
        }
    };

    const handleOnMouseEnter = () => {
        if (!matchesSm) setIsFlipped(true);
    };

    const handleOnMouseLeave = () => {
        if (!matchesSm) setIsFlipped(false);
    };

    useEffect(() => {
        if (matchesSm) {
            setIsFlipped(false);
        }
    }, [matchesSm]);

    return (
        <Grid item xs={12} sm={6} lg={4} textAlign="center" sx={{position: "relative"}}>
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                }}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
            >
                <Box
                    sx={{
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        height: "100%",
                        minHeight: 220,
                        transformStyle: "preserve-3d",
                        alignItems: "center",

                        transition: "transform 0.45s ease, box-shadow 0.25s ease, border-color 0.25s ease",

                        backgroundColor: alpha(theme.palette.primary.main, .05),

                        border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,

                        backdropFilter: "blur(6px)",

                        padding: "2rem",

                        borderRadius: "16px",

                        transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",

                        boxShadow: 3,

                        ...(!matchesSm && {
                            cursor: "pointer",
                            "&:hover": {
                                transform: isFlipped
                                    ? "rotateY(180deg)"
                                    : undefined,
                                boxShadow:
                                    6,
                                borderColor: alpha(theme.palette.common.white, 0.16),
                            },
                        }),
                    }}
                    onClick={handleOnClick}
                >
                    {/* FRONT SIDE */}
                    <Box
                        ref={frontSideBoxRef}
                        sx={{
                            display: isFlipped ? "none" : "flex",
                            width: "100%",
                            height: calculatedHeight && !matchesSm ? calculatedHeight : "auto",
                            gap: "12px",
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column",
                            position: "relative",

                            ...(!matchesSm && {
                                top: "50%",
                                transform: "translateY(-50%)",
                            }),
                        }}
                    >
                        {!props.loading ? (
                            <img
                                alt={props.item.title_en}
                                src={props.item.icon}
                                width={64}
                                style={{marginBottom: "6px"}}
                                onLoad={handleImageLoad}
                            />
                        ) : (
                            <Grid container justifyContent="center">
                                <Skeleton variant="circular" width="64px" height="64px"/>
                            </Grid>
                        )}

                        <Typography
                            variant="h5"
                            color="primary"
                            fontWeight={700}
                            sx={{
                                lineHeight: 1.25,
                                letterSpacing: "-0.02em",
                            }}
                            display="flex"
                            justifyContent="center"
                            width="100%"
                        >
                            {!props.loading ? (
                                props.item[`title_${i18n.language}`]
                            ) : (
                                <Skeleton animation="wave" width={`${getRandomNumber(60, 100)}%`}/>
                            )}
                        </Typography>
                    </Box>

                    {/* BACK SIDE */}
                    <Box
                        sx={{
                            position: "relative",
                            display: isFlipped || matchesSm ? "flex" : "none",
                            width: "100%",
                            height: calculatedHeight && !matchesSm ? calculatedHeight : "auto",
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column",

                            ...(!matchesSm && {
                                top: "50%",
                                transform: "rotateY(180deg) translateY(-50%)",
                            }),
                        }}
                    >
                        <MarkdownWrapper source={props.item[`subtitle_${i18n.language}`]} loading={props.loading}/>
                    </Box>

                    {/* hidden box for height calc */}
                    <Box
                        ref={hiddenBoxRef}
                        sx={{
                            position: "absolute",
                            paddingInline: "1em",
                            visibility: "hidden",
                            height: "auto",
                            width: "100%",
                            whiteSpace: "normal",
                            overflow: "hidden",
                            pointerEvents: "none",
                            top: "-9999px",
                        }}
                    >
                        <MarkdownWrapper source={props.item[`subtitle_${i18n.language}`]} loading={props.loading}/>
                    </Box>
                </Box>
            </Box>
        </Grid>
    );
};

export default WhyGenieGridItem;